import { useState } from 'react'
import { CheckCircleIcon, ClipboardDocumentIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { toast } from 'react-hot-toast'
import { formatFileSize, formatTimeRemaining } from '../utils/api'

interface UploadSuccessProps {
  data: {
    id: string;
    downloadUrl: string;
    filename: string;
    size: number;
    expiresAt?: string;
  };
  onReset: () => void;
}

export default function UploadSuccess({ data, onReset }: UploadSuccessProps) {
  const [copied, setCopied] = useState(false)

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(data.downloadUrl)
      setCopied(true)
      toast.success('Link copied to clipboard!')
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      toast.error('Failed to copy link')
    }
  }

  return (
    <div className="card max-w-2xl mx-auto text-center">
      <div className="space-y-6">
        <div className="flex flex-col items-center">
          <CheckCircleIcon className="h-16 w-16 text-green-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Upload Successful!</h2>
          <p className="text-gray-600">Your file has been uploaded and is ready to share.</p>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">File:</span>
            <span className="text-sm text-gray-900">{data.filename}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Size:</span>
            <span className="text-sm text-gray-900">{formatFileSize(data.size)}</span>
          </div>
          {data.expiresAt && (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Expires in:</span>
              <span className="text-sm text-gray-900">{formatTimeRemaining(data.expiresAt)}</span>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700 text-left">
            Share Link:
          </label>
          <div className="flex space-x-2">
            <input
              type="text"
              value={data.downloadUrl}
              readOnly
              className="input-field flex-1 font-mono text-sm"
            />
            <button
              onClick={copyToClipboard}
              className={`btn-primary px-3 ${copied ? 'bg-green-600 hover:bg-green-700' : ''}`}
            >
              <ClipboardDocumentIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex space-x-4">
          <button
            onClick={onReset}
            className="btn-primary flex-1 flex items-center justify-center space-x-2"
          >
            <ArrowPathIcon className="h-5 w-5" />
            <span>Upload Another File</span>
          </button>
          <a
            href={data.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary flex-1 text-center"
          >
            Test Download
          </a>
        </div>
      </div>
    </div>
  )
} 